<template>
  <div ref="topContainerRef" class="top-container container-fluid">
    <div class="container mx-auto px-4 pb-8" style="max-width: 100% !important;">
      <div class="search-bar-container mb-12">
        <div class="p-8">
          <h1 class="text-center mb-6 font-weight-800">{{ t('discover.title') }}</h1>
          <div class="max-w-6xl mx-auto mb-6">
            <div class="d-flex flex-column flex-md-row align-items-center gap-4 mb-4">
              <div class="relative flex-grow w-full">
                <svg class="search-icon cursor-pointer" @click="onEnterSearch" fill="none" height="24"
                  stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                </svg>
                <input v-model="searchKeyword" @keyup.enter="onEnterSearch" class="search-bar"
                  :placeholder="t('discover.searchPlaceholder')" type="text" value="">
                <b-icon v-if="searchKeyword" class="clear-search-icon" icon="x"
                  @click="onClearSearch"></b-icon>
              </div>
              <div class="flex gap-2 flex-wrap flex-md-nowrap">
                <button @click="expandFilters = !expandFilters" class="filter-options-btn">
                  <svg class="lucide lucide-sliders-horizontal mr-2 h-5 w-5" fill="none" height="24"
                    stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                    <line x1="21" x2="14" y1="4" y2="4"></line>
                    <line x1="10" x2="3" y1="4" y2="4"></line>
                    <line x1="21" x2="12" y1="12" y2="12"></line>
                    <line x1="8" x2="3" y1="12" y2="12"></line>
                    <line x1="21" x2="16" y1="20" y2="20"></line>
                    <line x1="12" x2="3" y1="20" y2="20"></line>
                    <line x1="14" x2="14" y1="2" y2="6"></line>
                    <line x1="8" x2="8" y1="10" y2="14"></line>
                    <line x1="16" x2="16" y1="18" y2="22"></line>
                  </svg>
                  {{ expandFilters ? t('discover.hideFilters') : t('discover.filters') }}
                </button>
                <b-dropdown id="sort-options" variant="outline" :text="`${t('discover.sort')}: ${t('discover.sortOptions.' + activeSort.key)}`"
                  class="sort-options-btn">
                  <b-dropdown-item v-for="sort of inactiveSortOptions" @click="changeSort(sort)">
                    <span class="pr-2"><img :src="sort.icon" /></span>{{ t(`discover.sortOptions.${sort.key}`) }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <div class="filters-expanded-view" v-if="expandFilters">
              <h3 class="font-weight-800 mb-3">{{ t('discover.categories') }}</h3>
              <b-row class="">
                <b-col class="mb-3" cols="12">
                  <h4 class="mb-2">{{ t('discover.categories') }}</h4>
                  <div class="d-flex flex-wrap">
                    <button v-for="category of workoutCategories"
                      @click="toggleCategory(category.categoryId)" :class="{'active': category.isActive}"
                      class="filter-btn">
                      <span class="label">{{ t(`category.${category.categoryName}`) }}</span>
                    </button>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <h4 class="mb-2">{{ t('discover.difficulty') }}</h4>
                  <div class="d-flex flex-wrap">
                    <button v-for="difficulty of difficultyOptions"
                      @click="toggleDifficulty(difficulty.name)" :class="{'active': difficulty.isActive}"
                      class="filter-btn">
                      <span class="label">{{ t(`discover.difficultyLevels.${difficulty.key}`) }}</span>
                    </button>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h4>{{ t('discover.duration') }}</h4>
                    <label v-if="selectedDuration">{{ t('discover.durationValue', { value: selectedDuration }) }}</label>
                  </div>
                  <b-form-input type="range" v-model="selectedDuration" :min="0" :max="maxDurationMins"
                    @change="changeDuration" class="d-block"></b-form-input>
                  <div class="d-flex justify-content-between align-items-center">
                    <label>{{ t('discover.minDuration') }}</label>
                    <label>{{ t('discover.maxDuration', { value: maxDurationMins }) }}</label>
                  </div>
                </b-col>
              </b-row>
            </div>
            <div class="active-filters-expanded-view"
              v-if="activeCategories.length || activeDifficulties.length || selectedDuration">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h3 class="font-weight-800 mb-2">{{ t('discover.activeFilters') }}</h3>
                <button class="text-btn" @click="clearFilter"><span class="label">{{ t('discover.clearAll') }}</span></button>
              </div>
              <div class="d-flex flex-wrap">
                <button class="bubble-shape" v-for="category of activeCategories"
                  @click="toggleCategory(category.categoryId)">
                  <span class="label">{{ t(`category.${category.categoryName}`) }}</span>
                  <b-icon class="ml-2" icon="x"></b-icon>
                </button>
                <button class="bubble-shape" v-for="difficulty of activeDifficulties"
                  @click="toggleDifficulty(difficulty.name)">
                  <span class="label">{{ t(`discover.difficultyLevels.${difficulty.key}`) }}</span>
                  <b-icon class="ml-2" icon="x"></b-icon>
                </button>
                <button class="bubble-shape" v-if="selectedDuration" @click="changeDuration(0)">
                  <span class="label">{{ t('discover.lessThanDuration', { value: selectedDuration }) }}</span>
                  <b-icon class="ml-2" icon="x"></b-icon>
                </button>
              </div>
            </div>
            <div class="popular-search-tags">
              <button v-for="popularTag of popularSearchTags" @click="toggleCategory(popularTag)"
                class="bubble-shape" :class="{'active': getCategory(popularTag).isActive}">
                <span class="pr-2"><img src="@/module_user/assets/discover/popular.svg" /></span>
                <span class="label">{{  t(`category.${getCategory(popularTag).categoryName}`) }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Categories -->
      <section v-show="workoutCategories.length" class="section-container">
    <h2 class="font-weight-800">{{ t('discover.categories') }}</h2>
    <div class="categories-carousel">
      <carousel
        ref="categoriesCarousel"
        :perPage="noOfSlides(300)"
        :autoplay="false"
        :loop="true"
        :paginationActiveColor="'#73BC44'"
        :spacePadding="spacePadding"
        :scrollPerPage="true"
        :autoplayHoverPause="false"
        :paginationEnabled="!isMobile"
        :paginationPosition="'top'"
        :paginationPadding="3"
      >
        <slide v-for="(category, index) in workoutCategories" :key="`categories-${index}`">
          <div class="mr-2 mr-md-3">
            <div
              class="slide-categories cursor-pointer"
              :class="{'active': category.isActive}"
              @click="toggleCategory(category.categoryId)"
            >
              <img
                :alt="category.categoryName"
                class="w-full h-40 object-cover"
                height="200"
                :src="category.categoryImageUrl"
                style="aspect-ratio: 200 / 200; object-fit: cover;"
                width="200"
              >
              <div class="category-label">
                <span class="text-white font-semibold text-lg">{{ t(`category.${category.categoryName}`) }}</span>
              </div>
            </div>
          </div>
        </slide>
      </carousel>
      <template v-if="!isMobile">
        <div class="control-button-left">
          <a class="arrow" @click.prevent="prevSlide($refs.categoriesCarousel)">
            <img src="@/module_b2b_users/assets/icons/chevron-left.svg" />
          </a>
        </div>
        <div class="control-button-right">
          <a class="arrow" @click.prevent="nextSlide($refs.categoriesCarousel)">
            <img src="@/module_b2b_users/assets/icons/chevron-right.svg" />
          </a>
        </div>
      </template>
    </div>
  </section>

  <section v-show="creators.length" class="section-container" v-if="!tenantWise">
    <div class="flex justify-between items-center mb-4">
      <h2 class="font-weight-800">{{ !searchKeyword ? t('discover.topTrainers') : t('discover.trainers') }}</h2>
      <button v-if="!searchKeyword" @click="toggleTenantWise" class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg--primary text-primary-foreground h-10 px-4 py-2">
        {{ t('discover.viewAllTrainers') }}
      </button>
    </div>
    <div class="creators-carousel">
      <carousel
        ref="creatorsCarousel"
        :perPage="noOfSlides(250)"
        :autoplay="false"
        :loop="true"
        :paginationActiveColor="'#73BC44'"
        :spacePadding="spacePadding"
        :scrollPerPage="true"
        :autoplayHoverPause="false"
        :paginationEnabled="!isMobile"
        :paginationPosition="'top'"
        :paginationPadding="3"
      >
        <slide v-for="(creator, index) of creators" :key="`top-creators-${index}`">
          <div class="mr-3 h-100">
            <div class="creator-card" @click="goToCreator(creator.redirect_url)">
              <span class="creator-image relative flex shrink-0 overflow-hidden rounded-full w-20 h-20 mb-3">
                <img alt="" class="aspect-square h-full w-full" :src="creator.profile_pic_url">
              </span>
              <h4 class="mb-1 text-center">{{ creator.name }}</h4>
              <p class="total-video-count text-xs text-muted-foreground">
                {{ t('discover.videosCount', { count: creator.total_videos || 0 }) }}
              </p>
            </div>
          </div>
        </slide>
      </carousel>
      <template v-if="!isMobile">
        <div class="control-button-left">
          <a class="arrow" @click.prevent="prevSlide($refs.creatorsCarousel)">
            <img src="@/module_b2b_users/assets/icons/chevron-left.svg" />
          </a>
        </div>
        <div class="control-button-right">
          <a class="arrow" @click.prevent="nextSlide($refs.creatorsCarousel)">
            <img src="@/module_b2b_users/assets/icons/chevron-right.svg" />
          </a>
        </div>
      </template>
    </div>
  </section>

  <section class="section-container" v-if="!tenantWise">
    <div class="flex items-center justify-between mb-4">
      <h2 class="font-weight-800">{{ t('discover.workouts') }}</h2>
      <div class="flex items-center text-muted-foreground">
        <svg
          class="lucide lucide-video w-5 h-5 mr-2"
          fill="none"
          height="24"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"></path>
          <rect height="12" rx="2" width="14" x="2" y="6"></rect>
        </svg>
        <span class="text-sm font-medium">{{ t('discover.availableWorkouts', { count: totalRecords }) }}</span>
      </div>
    </div>
    <div v-if="noContentFound" class="text-center">
      <img src="@/module_user/assets/images/empty_basket.png" />
      <h4 class="pt-3">{{ t('discover.noContent.title') }}</h4>
      <p class="pt-2">{{ t('discover.noContent.message') }}</p>
    </div>
    <b-row v-else>
      <b-col
        v-for="(video, index) in workouts"
        :key="index"
        class="mb-4"
        cols="12"
        sm="6"
        md="4"
        xl="3"
      >
        <WorkoutVideo
          :id="video.id"
          :categories="video.categories"
          :duration="video.duration"
          :intensity="video.intensity"
          :is-locked="video.isLocked"
          :locked-type="video.lockedType"
          :tenantName="video.tenantName"
          :tenantId="video.tenantId"
          :thumbnail="video.thumbnail"
          :title="video.title"
          :showPrev="video.showPrev"
          :videoViews="video.videoViews"
          :needTranslation="video.needTranslation"
        />
      </b-col>
    </b-row>
  </section>
      <!-- Tenant wise workouts -->
  <section v-else>
  <div class="flex justify-between items-center mb-4">
    <h2 class="font-weight-800">{{ t('discover.allTrainers') }}</h2>
    <button @click="toggleTenantWise" class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg--primary text-primary-foreground hover:bg--primary/90 h-10 px-4 py-2">
      {{ t('discover.close') }}
    </button>
  </div>
  <template v-if="noContentFound">
    <div class="text-center">
      <img src="@/module_user/assets/images/empty_basket.png" />
      <h4 class="pt-3">{{ t('discover.noContent.title') }}</h4>
      <p class="pt-2">{{ t('discover.noContent.message') }}</p>
    </div>
  </template>
  <template v-else>
    <section class="section-container" v-for="(item, tidx) in tenantWiseWorkouts">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="tenant-profile">
          <span class="relative flex shrink-0 overflow-hidden rounded-full mr-2">
            <img alt="" class="aspect-square h-full w-full" :src="item.profileUrl">
          </span>
          <h3 class="notranslate font-weight-800">{{ item.name }}</h3>
        </div>
        <div v-if="item.redirectUrl" @click="goToCreator(item.redirectUrl)" class="cursor-pointer sub-title ml-3 text-decoration-none font-weight-700" style="color: #73bc44;">
          {{ t('discover.viewProfile') }}
        </div>
      </div>
      <div class="tenant-videos-container" @mouseenter="mouseoverOn=tidx" @mouseleave="mouseoverOn=null">
        <carousel
          :ref="`tenantVideosRefs`"
          :perPage="noOfSlides(325)"
          :autoplay="false"
          :loop="true"
          :paginationActiveColor="'#73BC44'"
          :spacePadding="spacePadding"
          :scrollPerPage="true"
          :autoplayHoverPause="false"
          :paginationEnabled="!isMobile"
          :paginationPosition="'top'"
          :paginationPadding="3"
        >
          <slide v-for="(video, vidx) of item.content" :key="`tenant-videos-${tidx}-${vidx}`">
            <div class="workout-container h-100">
              <WorkoutVideo
                :id="video.id"
                :categories="video.categories"
                :duration="video.duration"
                :intensity="video.intensity"
                :is-locked="video.isLocked"
                :locked-type="video.lockedType"
                :thumbnail="video.thumbnail"
                :title="video.title"
                :description="video.description"
                :tenant-name="item.name"
                :tenant-id="item.id"
                :videoViews="video.videoViews"
                :needTranslation="video.needTranslation"
              ></WorkoutVideo>
            </div>
          </slide>
        </carousel>
        <template v-if="!isMobile && mouseoverOn === tidx">
          <div class="control-button-left">
            <a class="arrow" @click.prevent="prevSlide($refs.tenantVideosRefs[tidx])">
              <img src="@/module_b2b_users/assets/icons/chevron-left.svg" />
            </a>
          </div>
          <div class="control-button-right">
            <a class="arrow" @click.prevent="nextSlide($refs.tenantVideosRefs[tidx])">
              <img src="@/module_b2b_users/assets/icons/chevron-right.svg" />
            </a>
          </div>
        </template>
      </div>
    </section>
  </template>
</section>
</div>
    <loader v-if="isLoading"></loader>
  </div>
</template>
<script>
import { i18nComponentMixin } from "@/i18n/mixin";
import {discoverService} from "@/module_user/api/discover";
import WorkoutVideo from "@/module_user/components/Discover/WorkoutVideo.vue";
function debounce(func, delay) {
  let timeoutId;

  return function () {
    const context = this;
    const args = arguments;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(function () {
      func.apply(context, args);
    }, delay);
  };
}
export default {
  components: {
    WorkoutVideo
  },
  mixins:[i18nComponentMixin('views/DiscoverV2')],
  data() {
    return {
      difficultyOptions: [{
        key :"beginner",
        name: 'Beginner',
        isActive: false
      }, {
        key :"intermediate",
        name: 'Intermediate',
        isActive: false
      }, {
        key :"advanced",
        name: 'Advanced',
        isActive: false
      }],
      sortOptions: [{
        key: 'popularity',
        name: 'Popularity',
        isActive: true,
        sortBy: 'popularity',
        sortDirection: 'DESC',
        icon: require('@/module_user/assets/discover/popular.svg')
      }, {
        key: 'difficulty-high-to-low',
        name: 'Difficulty: High to Low',
        isActive: false,
        sortBy: 'difficulty',
        sortDirection: 'DESC',
        icon: require('@/module_user/assets/discover/difficult.svg')
      }, {
        key: 'difficulty-low-to-high',
        name: 'Difficulty: Low to High',
        isActive: false,
        sortBy: 'difficulty',
        sortDirection: 'ASC',
        icon: require('@/module_user/assets/discover/difficult.svg')
      }, {
        key: 'duration-high-to-low',
        name: 'Duration: High to Low',
        isActive: false,
        sortBy: 'duration',
        sortDirection: 'DESC',
        icon: require('@/module_user/assets/discover/duration.svg')
      },{
        key: 'duration-low-to-high',
        name: 'Duration: Low to High',
        isActive: false,
        sortBy: 'duration',
        sortDirection: 'ASC',
        icon: require('@/module_user/assets/discover/duration.svg')
      }],
      maxDurationMins: 120,
      popularSearchTags: [],
      workoutCategories: [],
      creators: [],
      workouts: [],
      tenantWiseWorkouts: [],

      isLoading: true,
      noContentFound: false,
      similarVideos: [],
      searchKeyword: '',
      selectedDuration: 0,
      onlyVideoIds: null, // on text search, api can return all the videos, useful in next api call to have pagination support.

      totalPages: 1,
      currentPage: 1,
      pageSize: 50,
      totalRecords: 1,
      tenantWise: false,

      sortBy: "popularity",
      sortDirection: "DESC",

      expandFilters: false,
      mouseoverOn: null,
    }
  },
  computed: {
    requestBody() {
      let filters;
      if (this.activeCategories.length)
        filters = Object.assign({},{categories: this.activeCategories.map(c => c.categoryId)});
      if (this.activeDifficulties.length)
        filters = Object.assign({}, filters, {intensities: this.activeDifficulties.map(d => d.name)});
      if (this.selectedDuration)
        filters = Object.assign({}, filters, {duration: this.selectedDuration * 60});

      const body = {
        page: this.currentPage,
        pageSize: this.pageSize,
        filters,
        searchKeyword: this.searchKeyword,
        onlyVideoIds: this.onlyVideoIds,
        tenantWise: this.tenantWise,
        sortBy: this.sortBy,
        sortDirection: this.sortDirection
      };
      let filtered = {};
      Object.keys(body).filter(key => body[key] != null)
          .forEach(key => filtered[key] = body[key]);
      return filtered;
    },
    isAnonymous() {
      if (this.$store.state.userInfo) {
        return this.$store.state.userInfo.isAnonymousUser;
      } else {
        return true;
      }
    },
    spacePadding() {
      return !this.isMobile ? 55 : 25;
    },
    isMobile() {
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      return isAndroid || isiOS;
    },
    categoryLimiter() {
      return window.matchMedia("(orientation: landscape)").matches ? 50 : 40;
    },
    categoryPerScreen() {
      return window.matchMedia("(orientation: landscape)").matches ? 5 : 2;
    },
    activeSort() {
      return this.sortOptions.find(sort => sort.isActive);
    },
    inactiveSortOptions() {
      return this.sortOptions.filter(sort => !sort.isActive);
    },
    activeCategories() {
      return this.workoutCategories.filter(c => c.isActive);
    },
    activeDifficulties() {
      return this.difficultyOptions.filter(d => d.isActive);
    },
    userInfo() {
      return this.$store.state.userInfo || {};
    }
  },
  methods: {
    loadDetails(params) {
      this.isLoading = true;
      return discoverService.details().then(data => {
        const {workoutCategories, popularSearchTags} = data;
        workoutCategories.forEach(el => {
          if(el.categoryName === params.activeCat){
            el.isActive = true
          }
        })
        this.workoutCategories = (workoutCategories || []).map(category => Object.assign({isActive: false}, category));
        this.popularSearchTags = (popularSearchTags || []).map(tag => tag.categoryId);
      }).finally(_ => {
        this.isLoading = false;
      })
    },
    findWorkouts: debounce(function (options) {
      this.isLoading = true;
      const body = Object.assign({}, this.requestBody, options);
      return discoverService.find(body).then(data => {
        const {totalCount, currentPage, totalPages, results, onlyVideoIds} = data;
        if (this.tenantWise) this.tenantWiseWorkouts = [...this.tenantWiseWorkouts, ...results];
        else this.workouts = [...this.workouts, ...results];
        this.totalPages = totalPages;
        this.totalRecords = totalCount;
        this.onlyVideoIds = onlyVideoIds;
        this.currentPage = currentPage;
        if (this.tenantWise) this.noContentFound = this.tenantWiseWorkouts.length === 0 && this.currentPage === 1;
        else {
          this.noContentFound = this.workouts.length === 0 && this.currentPage === 1;
          const searchKeyword = (this.userInfo?.interests || []).join(', ') || this.userInfo.goal || 'Dance, Toning, Exercise, Strength, Steps';
          if (searchKeyword) {
            discoverService.find({searchKeyword}).then(data => {
              this.similarVideos = data.results;
            })
          }
        }
      }).finally(_ => {
        this.isLoading = false;
      })
    }, 300),
    findCreators: debounce(function () {
      this.isLoading = true;
      const body = {
        page: 1,
        pageSize: 15,
        searchKeyword: this.searchKeyword,
      }
      return discoverService.findCreators(body).then(data => {
        const {totalCount, currentPage, totalPages, results} = data;
        this.creators = [...this.creators, ...results];
      }).finally(_ => {
        this.isLoading = false;
      })
    }, 300),
    onEnterSearch() {
      this.reset();
      this.getSearchResults();
    },
    onClearSearch() {
      this.searchKeyword = '';
      this.reset();
      this.getSearchResults();
    },
    getSearchResults() {
      this.reset();
      this.findWorkouts();
      if (!this.requestBody.filters) this.findCreators();
    },
    reset() {
      this.onlyVideoIds = null;
      this.tenantWiseWorkouts = [];
      this.workouts = [];
      this.creators = [];
      this.currentPage = 1;
    },
    toggleCategory(categoryId) {
      const category = this.getCategory(categoryId);
      category.isActive = !category.isActive;
      this.getSearchResults();
    },
    toggleDifficulty(name) {
      const difficulty = this.getDifficulty(name);
      difficulty.isActive = !difficulty.isActive;
      this.getSearchResults();
    },
    toggleTenantWise() {
      this.tenantWise = !this.tenantWise;
      this.getSearchResults();
    },
    changeDuration(selectedDuration) {
      this.selectedDuration = selectedDuration;
      this.getSearchResults();
    },
    changeSort(sort) {
      this.sortBy = sort.sortBy;
      this.sortDirection = sort.sortDirection;
      const lastActive = this.sortOptions.find(sort => sort.isActive);
      if (lastActive) lastActive.isActive = false;
      sort.isActive = true;
      this.getSearchResults();
    },
    clearFilter() {
      this.workoutCategories.map(c => Object.assign(c, {isActive: false}));
      this.difficultyOptions.map(d => Object.assign(d, {isActive: false}));
      this.selectedDuration = 0;
      this.getSearchResults();
    },
    getCategory(id) {
      return this.workoutCategories.find(c => c.categoryId === id);
    },
    getDifficulty(name) {
      return this.difficultyOptions.find(d => d.name === name);
    },
    nextSlide(ref) {
      ref.goToPage(ref.getNextPage());
    },
    prevSlide(ref) {
      ref.goToPage(ref.getPreviousPage());
    },
    goToCreator(redirectPath) {
      if (redirectPath) this.$router.push({
        path: `/${redirectPath}`
      });
    },
    onBottom() {
      if (!this.isLoading && this.currentPage < this.totalPages) {
        this.findWorkouts({
          page: this.currentPage + 1
        })
      }
    },
    noOfSlides(size) {
      if (!this.$refs.topContainerRef) return 2;
      return Math.floor(this.$refs.topContainerRef.offsetWidth / size);

    }
  },
  async mounted() {
    const params = this.$route.query;
    await this.loadDetails(params);
    const {categoryId, search} = this.$route.query;
    if (categoryId) this.toggleCategory(Number(categoryId));
    if (search) this.searchKeyword = search
    this.getSearchResults();
  }
}
</script>
<style lang="scss" scoped>
.top-container {
  --grey-background: #efefef;
  --background: 0 0% 100%;
  --foreground: 240 10% 3.9%;
  --muted: 240 4.8% 95.9%;
  --muted-foreground: 240 3.8% 46.1%;
  --card: 0 0% 100%;
  --card-foreground: 240 10% 3.9%;
  --popover: 0 0% 100%;
  --popover-foreground: 240 10% 3.9%;
  --border: 240 5.9% 90%;
  --border-color: 240 5.9% 90%;
  --primary: 240 5.9% 10%;
  --primary-foreground: 0 0% 98%;
  --secondary: 240 4.8% 95.9%;
  --secondary-foreground: 240 5.9% 10%;
  --accent: 240 4.8% 95.9%;
  --accent-foreground: ;
  --destructive: 0 84.2% 60.2%;
  --destructive-foreground: 0 0% 98%;
  --warning: 35, 100%, 52%;
  --warning-foreground: 0 0% 9%;
  --ring: 240 5% 64.9%;
  --radius: 0.5rem;
  --chart-1: 173 58% 39%;
  --chart-2: 12 76% 61%;
  --chart-3: 197 37% 24%;
  --chart-4: 43 74% 66%;
  --chart-5: 27 87% 67%;
  --sidebar-background: 0 0% 98%;
  --sidebar-foreground: 240 5.3% 26.1%;
  --sidebar-primary: 240 5.9% 10%;
  --sidebar-primary-foreground: 0 0% 98%;
  --sidebar-accent: 240 4.8% 95.9%;
  --sidebar-accent-foreground: 240 5.9% 10%;
  --sidebar-border: 220 13% 91%;
  --sidebar-ring: 240 5% 64.9%;

  --tw-border-spacing-x: 0;
  --tw-border-spacing-y: 0;
  --tw-translate-x: 0;
  --tw-translate-y: 0;
  --tw-rotate: 0;
  --tw-skew-x: 0;
  --tw-skew-y: 0;
  --tw-scale-x: 1;
  --tw-scale-y: 1;
  --tw-pan-x: ;
  --tw-pan-y: ;
  --tw-pinch-zoom: ;
  --tw-scroll-snap-strictness: proximity;
  --tw-gradient-from-position: ;
  --tw-gradient-via-position: ;
  --tw-gradient-to-position: ;
  --tw-ordinal: ;
  --tw-slashed-zero: ;
  --tw-numeric-figure: ;
  --tw-numeric-spacing: ;
  --tw-numeric-fraction: ;
  --tw-ring-inset: ;
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: rgb(59 130 246 / 0.5);
  --tw-ring-offset-shadow: 0 0 #0000;
  --tw-ring-shadow: 0 0 #0000;
  --tw-shadow: 0 0 #0000;
  --tw-shadow-colored: 0 0 #0000;
  --tw-blur: ;
  --tw-brightness: ;
  --tw-contrast: ;
  --tw-grayscale: ;
  --tw-hue-rotate: ;
  --tw-invert: ;
  --tw-saturate: ;
  --tw-sepia: ;
  --tw-drop-shadow: ;
  --tw-backdrop-blur: ;
  --tw-backdrop-brightness: ;
  --tw-backdrop-contrast: ;
  --tw-backdrop-grayscale: ;
  --tw-backdrop-hue-rotate: ;
  --tw-backdrop-invert: ;
  --tw-backdrop-opacity: ;
  --tw-backdrop-saturate: ;
  --tw-backdrop-sepia: ;
  --tw-contain-size: ;
  --tw-contain-layout: ;
  --tw-contain-paint: ;
  --tw-contain-style: ;

  max-width: 100% !important;

}

.container {
  width: 100%;
  margin-right: auto;
  margin-left: auto
}

@media (min-width: 1400px) {
  .container {
    max-width: 1400px
  }
}

.\!visible {
  visibility: visible !important
}

.fixed {
  position: fixed
}

.absolute {
  position: absolute
}

.relative {
  position: relative
}

.inset-0 {
  inset: 0px
}

.left-4 {
  left: 1rem
}

.top-0 {
  top: 0px
}

.top-1\/2 {
  top: 50%
}

.bottom-2 {
  bottom: 0.5rem
}

.right-2 {
  right: 0.5rem
}

.z-\[100\] {
  z-index: 100
}

.mx-auto {
  margin-left: auto;
  margin-right: auto
}

.mb-12 {
  margin-bottom: 3rem
}

.mb-4 {
  margin-bottom: 1rem
}

.mb-6 {
  margin-bottom: 1.5rem
}

.mr-2 {
  margin-right: 0.5rem
}

.mt-4 {
  margin-top: 1rem
}

.mb-1 {
  margin-bottom: 0.25rem
}

.mb-2 {
  margin-bottom: 0.5rem
}

.mb-3 {
  margin-bottom: 0.75rem
}

.ml-2 {
  margin-left: 0.5rem
}

.mr-1 {
  margin-right: 0.25rem
}

.mt-auto {
  margin-top: auto
}

.mt-2 {
  margin-top: 0.5rem
}

.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2
}

.inline-block {
  display: inline-block
}

.flex {
  display: flex
}

.inline-flex {
  display: inline-flex
}

.grid {
  display: grid
}

.aspect-square {
  aspect-ratio: 1 / 1
}

.h-10 {
  height: 2.5rem
}

.h-40 {
  height: 10rem
}

.h-5 {
  height: 1.25rem
}

.h-6 {
  height: 1.5rem
}

.h-9 {
  height: 2.25rem
}

.h-20 {
  height: 5rem
}

.h-4 {
  height: 1rem
}

.h-48 {
  height: 12rem
}

.h-full {
  height: 100%
}

.max-h-screen {
  max-height: 100vh
}

.w-5 {
  width: 1.25rem
}

.w-6 {
  width: 1.5rem
}

.w-full {
  width: 100%
}

.w-20 {
  width: 5rem
}

.w-4 {
  width: 1rem
}

.max-w-6xl {
  max-width: 72rem
}

.flex-1 {
  flex: 1 1 0%
}

.shrink-0 {
  flex-shrink: 0
}

.flex-grow {
  flex-grow: 1
}

.-translate-y-1\/2 {
  --tw-translate-y: -50%;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))
}

.transform {
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))
}

.cursor-pointer {
  cursor: pointer
}

.grid-cols-1 {
  grid-template-columns:repeat(1, minmax(0, 1fr))
}

.grid-cols-2 {
  grid-template-columns:repeat(2, minmax(0, 1fr))
}

.grid-cols-3 {
  grid-template-columns:repeat(3, minmax(0, 1fr))
}

.flex-col {
  flex-direction: column
}

.flex-col-reverse {
  flex-direction: column-reverse
}

.flex-wrap {
  flex-wrap: wrap
}

.items-center {
  align-items: center
}

.justify-center {
  justify-content: center
}

.justify-between {
  justify-content: space-between
}

.gap-2 {
  gap: 0.5rem
}

.gap-4 {
  gap: 1rem
}

.gap-6 {
  gap: 1.5rem
}

.overflow-hidden {
  overflow: hidden
}

.whitespace-nowrap {
  white-space: nowrap
}

.rounded-full {
  border-radius: 9999px
}

.rounded-md {
  border-radius: calc(var(--radius) - 2px)
}

.rounded-sm {
  border-radius: calc(var(--radius) - 6px)
}

.border {
  border-width: 1px
}

.border-2 {
  border-width: 2px
}

.border--input {
  border-color: hsl(var(--border-color))
}

.border-transparent {
  border-color: transparent
}

.bg-black {
  --tw-bg-opacity: 1;
  background-color: rgb(0 0 0 / var(--tw-bg-opacity))
}

.bg-card {
  background-color: hsl(var(--card))
}

.bg--primary {
  background-color: hsl(var(--primary))
}

.bg--secondary {
  background-color: hsl(var(--secondary))
}

.bg-muted {
  background-color: hsl(var(--muted))
}

.bg-opacity-50 {
  --tw-bg-opacity: 0.5
}

.bg-opacity-70 {
  --tw-bg-opacity: 0.7
}

.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops))
}

.to-muted {
  --tw-gradient-to: hsl(var(--muted)) var(--tw-gradient-to-position)
}

.object-cover {
  object-fit: cover
}

.p-4 {
  padding: 1rem
}

.p-8 {
  padding: 2rem
}

.p-6 {
  padding: 1.5rem
}

.p-1 {
  padding: 0.25rem
}

.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem
}

.py-6 {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem
}

.px-2 {
  padding-left: 0.5rem;
  padding-right: 0.5rem
}

.px-2\.5 {
  padding-left: 0.625rem;
  padding-right: 0.625rem
}

.py-0\.5 {
  padding-top: 0.125rem;
  padding-bottom: 0.125rem
}

.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem
}

.py-1\.5 {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem
}

.pb-8 {
  padding-bottom: 2rem
}

.pl-12 {
  padding-left: 3rem
}

.pr-4 {
  padding-right: 1rem
}

.pt-12 {
  padding-top: 3rem
}

.text-center {
  text-align: center
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem
}

.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem
}

.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem
}

.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem
}

.font-bold {
  font-weight: 700
}

.font-medium {
  font-weight: 500
}

.font-semibold {
  font-weight: 600
}

.text-card-foreground {
  color: hsl(var(--card-foreground))
}

.text-foreground {
  color: hsl(var(--foreground))
}

.text-muted-foreground {
  color: hsl(var(--muted-foreground))
}

.text-primary {
  color: hsl(var(--primary))
}

.text-primary-foreground {
  color: hsl(var(--primary-foreground))
}

.text-secondary-foreground {
  color: hsl(var(--secondary-foreground))
}

.text-white {
  --tw-text-opacity: 1;
  color: rgb(255 255 255 / var(--tw-text-opacity))
}

.\!opacity-100 {
  opacity: 1 !important
}

.text-muted-foreground::placeholder {}

h1, h2, h3, h4, h5, h6, label, .h1, .h2, .h3, .h4, .h5, .h6, .label {
  margin: 0;
}
h1, .h1 {
  font-size: 1.875rem;
  line-height: 2.25rem;
}

h2, .h2 {
  font-size: 1.5rem;
  line-height: 2rem;
}

h3, .h3 {
  font-size: 1rem;
  line-height: 1.5rem;
}

h4, p, label, .h4, .p, .label {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

::v-deep .btn:focus{
  box-shadow: none !important;
}

.search-bar-container {
  border: 1px solid hsl(var(--border-color));
  border-radius: var(--radius);
  background-color: #ffffff;

  ::v-deep .dropdown-menu.show {
    background-color: inherit !important;
    color: inherit !important;

    .dropdown-item {
      color: inherit !important;
      background-color: inherit !important;
      display: flex;
    }
  }

  .filters-expanded-view, .active-filters-expanded-view {
    background-color: hsl(0deg 0% 97.79%);
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: var(--radius);
  }

  .search-bar, .filter-options-btn, .sort-options-btn {
    white-space: nowrap;
    background-color: hsl(var(--background));
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5rem 1rem;
    border: 2px solid hsl(var(--border-color)) !important;
    border-radius: 9999px;
    height: 52px;
  }
  .search-bar {
    padding: 0 3rem 0 3rem;
    font-size: 1.25rem;
    line-height: 1.75rem;
    width: 100%;
  }
  .search-icon, .clear-search-icon {
    position: absolute;
    display: inline-block;
    top: 50%;
    transform: translate(0, -50%);
  }
  .search-icon {
    left: 1rem;
    width: 24px;
    height: 24px;
  }
  .clear-search-icon {
    cursor: pointer;
    right: 1rem;
    width: 32px;
    height: 32px;
  }
  .text-btn {
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    padding: 0 0.75rem;
    height: 2.25rem;
    background-color: inherit;
  }
  .filter-btn, .bubble-shape {
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    padding: 0 0.75rem;
    height: 2.25rem;
    margin: 0 0.5rem 0.5rem 0;
    border: 1px solid hsl(var(--border-color));
    border-radius: calc(var(--radius) - 2px);
  }
  .filter-btn {
    &.active {
      background: black;
      color: white;
    }
  }
  .bubble-shape {
    border-radius: 9999px;
  }
  .popular-search-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;

    .active {
      border: 2px solid #73bc44;
    }
  }
}
.categories-carousel {
  .active {
    border: 4px solid #73bc44;
  }
}
.creators-carousel {
  .creator-card {
    border: 1px solid hsl(var(--border-color));
    border-radius: var(--radius);
    background-color: hsl(var(--background));
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    height: 100%;

    img {
      background-color: var(--grey-background);
    }
    .total-video-count {
      margin-top: auto;
    }
  }
}
.tenant-profile {
  display: flex;
  align-items: center;

  span {
    width: 32px;
    height: 32px;
    img {
      background-color: var(--grey-background);
    }
  }
}
::v-deep .VueCarousel {
  .VueCarousel-inner {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .VueCarousel-pagination {
    text-align: right !important;

    .VueCarousel-dot-container {
      margin-top: 0 !important;
      button {
        width: 30px !important;
        height: 3px !important;
        background-color: #969B92 !important;
      }
      .VueCarousel-dot {
        border-radius: 0 !important;
      }
      .VueCarousel-dot--active {
        background-color: #73BC44 !important;
      }
    }
  }
  .VueCarousel-dot-container {
    display: flex;
    align-items: center;
    justify-content: end;
  }
}

.section-container {
  position: relative;
  //background: #F4F6F9;
  overflow: hidden;
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;

  .categories-carousel {
    position: relative;
  }
  .creators-carousel {
    position: relative;

    .creator-image {
      border: solid 2px #eaeaea;
    }
  }
  .tenant-videos-container {
    position: relative;

    .workout-container {
      //min-width: 350px;
      margin-right: 1rem;
    }
  }
  .workout-video {
    margin-bottom: 1rem;
  }
}
.creators-carousel:hover > .control-button-left,
.creators-carousel:hover > .control-button-right,
.categories-carousel:hover > .control-button-left,
.categories-carousel:hover > .control-button-right,
.tenant-videos-container> .control-button-left,
.tenant-videos-container> .control-button-right
{
  background: #141513;
  opacity: 0.75;
  backdrop-filter: blur(8px);
  .arrow {
    display: block;
    cursor: pointer;
    img {
      color: #FFFFFF;
    }
  }
  .arrow:hover {
    img {
      height: 62px;
      width: 62px;
    }
  }
}

.slide-categories {
  border-radius: 15px;
  height: auto;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: block;

  img {
    width: 100%;
    min-height: 150px;
    max-height: 150px;
    border-radius: 15px;
    object-fit: cover;
  }


  .category-label {
    position: absolute;
    bottom: 0px;
    left: 0;
    width: 100%;
    height: 70px;
    padding: 20px 30px;
    // background-color: rgba(0, 0, 0,0.05);
    background: linear-gradient(0deg, rgba(0,0,0, 0.4) 0%, rgba(255,255,255,0) 100%);

    span {
      color: #fff;
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
    }
  }
}

.category-label {
  position: absolute;
  bottom: 0px;
  left: 0;
  width: 100%;
  height: 70px;
  padding: 20px 30px;
  // background-color: rgba(0, 0, 0,0.05);
  background: linear-gradient(0deg, rgba(0,0,0, 0.4) 0%, rgba(255,255,255,0) 100%);

  span {
    color: #fff;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }
}

.control-button-right, .control-button-left {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: absolute;
  top: 0;
  margin-top: 0;
  width: 48px;
  background-color: transparent;
  .arrow {
    display: none;
  }
}
.control-button-left {
  border-radius: 0px 6px 6px 0px;
}
.control-button-right {
  left: calc(100% - 48px);
  border-radius: 6px 0px 0px 6px;
}

.font-weight-800 {
  font-weight: 800!important;
}

@media (max-width: 767px) {
  .slide-categories {
    border-radius: 12px;

    img {
      min-height: 100px;
      max-height: 100px;
    }

    .category-label {
      span {
        font-size: 14px;
        line-height: 18px;
        display: block;
      }
    }
  }
}
</style>